"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import MailIcon from "@mui/icons-material/MailOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccountsOutlined";
import CampaignIcon from "@mui/icons-material/CampaignOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import ResourcesIcon from "@/components/icons/ResourcesIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useActivePartner } from "@/contexts/ActivePartnerContext";
import { getPortalUserContext } from "@/lib/getPortalUserContext";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { PartnerSwitcher } from "@/components/partnerSwitcher/PartnerSwitcher";
import type { ReactNode } from "react";

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 64;
const TRANSITION = "width 225ms cubic-bezier(0.4, 0, 0.6, 1)";

const ACTIVE_BG = "rgba(200,49,42,0.08)";
const ACTIVE_BORDER = "#f36944";

type NavItem = {
    label: string;
    href: string;
    icon: ReactNode;
    show: boolean;
    disabled?: boolean;
};

type NavSection = {
    heading: string;
    items: NavItem[];
    show: boolean;
};

function isActive(pathname: string, href: string): boolean {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
}


function getInitials(displayName: string | null | undefined, email: string | undefined): string {
    if (displayName) {
        return displayName.trim().split(/\s+/).map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return (email?.[0] ?? "?").toUpperCase();
}

export const LoggedInAppLayout = ({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) => {
    const [open, setOpen] = useState(true);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [partnerAnchor, setPartnerAnchor] = useState<HTMLElement | null>(null);
    const { user, logout } = useAuth();
    const { activePartnerCode, activePartnerName, activeGrant, partnerOptions, partnersLoading, setActivePartnerCode } = useActivePartner();
    const router = useRouter();
    const pathname = usePathname();
    const isNarrow = useMediaQuery("(max-width:1024px)");
    const collapsed = !open;

    useEffect(() => {
        if (isNarrow) setOpen(false);
    }, [isNarrow]);

    const grants = user?.grants ?? [];
    const hasMultiplePartners = grants.length > 1;
    const partnerDisplayName = activePartnerName ?? activePartnerCode;

    const { isVidivetAdmin, canUser } = getPortalUserContext(user, activeGrant);

    const navSections: NavSection[] = [
        {
            heading: "Overview",
            show: true,
            items: [
                { label: "Dashboard", href: "/", icon: <DashboardIcon />, show: true },
            ],
        },
        {
            heading: "Pet owners",
            show: canUser.viewClients,
            items: [
                { label: "Pet owner data", href: "/clients", icon: <GroupIcon />, show: true },
                { label: "Conversations", href: "/conversations", icon: <ChatBubbleOutlineOutlinedIcon />, show: true, disabled: false },
            ],
        },
        {
            heading: "Insights",
            show: canUser.viewClients,
            items: [
                { label: "Performance", href: "/performance", icon: <TrendingUpIcon />, show: true },
            ],
        },
        {
            heading: "Support",
            show: true,
            items: [
                { label: "Resources", href: "/resources", icon: <ResourcesIcon />, show: true },
                { label: "Get in touch", href: "/get-in-touch", icon: <MailIcon />, show: true },
            ],
        },
        {
            heading: "Settings",
            show: canUser.browseUsers,
            items: [
                { label: "Users", href: "/users", icon: <PeopleIcon />, show: canUser.browseUsers },
                { label: "Audit Log", href: "/audit-log", icon: <HistoryIcon />, show: canUser.viewAuditLog },
                { label: "Account", href: "/account", icon: <ManageAccountsIcon />, show: false, disabled: true },
            ],
        },
        {
            heading: "Admin",
            show: isVidivetAdmin,
            items: [
                { label: "Announcement Banner", href: "/admin/announcement-banner", icon: <CampaignIcon />, show: true },
                { label: "Resources", href: "/admin/resources", icon: <ResourcesIcon />, show: true },
            ],
        },
    ];

    const flatNavItems = navSections
        .filter((s) => s.show)
        .flatMap((s) => s.items.filter((i) => i.show));

    return (
        <Box className="print-layout-root" sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            {/* App Bar — full width, sits above everything */}
            <AppBar position="static" sx={{ flexShrink: 0, borderRadius: "12px 12px 0 0", overflow: "hidden" }}>
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    {/* Left: drawer toggle + logo + title */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={() => setOpen((prev) => !prev)}
                            aria-label={open ? "collapse menu" : "expand menu"}
                        >
                            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                        <Box
                            component="img"
                            src={isNarrow ? "/vidivet-logo.svg" : "/vidivet-logo-full.svg"}
                            alt="VidiVet"
                            sx={{ height: 24 }}
                        />
                        {!isNarrow && (
                            <Typography variant="h6" noWrap component="div">
                                Partner Portal
                            </Typography>
                        )}
                    </Box>

                    {/* Right: partner switcher + user avatar */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {partnerDisplayName && (
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    color: "primary.contrastText",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    maxWidth: { xs: 150, md: 300 },
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                {partnerDisplayName}
                            </Typography>
                        )}

                        {isVidivetAdmin && (
                            <PartnerSwitcher
                                options={partnerOptions}
                                activeCode={activePartnerCode}
                                activeName={partnerDisplayName}
                                onSelect={setActivePartnerCode}
                            />
                        )}

                        {!isVidivetAdmin && hasMultiplePartners && (
                            <>
                                <IconButton
                                    onClick={(e) => setPartnerAnchor(e.currentTarget)}
                                    aria-label="switch partner"
                                    sx={{
                                        color: "warning.dark",
                                        bgcolor: "white",
                                        borderRadius: "24px",
                                        px: 1,
                                        py: 0.75,
                                        boxShadow: 2,
                                        "&:hover": { bgcolor: "grey.100" },
                                    }}
                                >
                                    <BusinessIcon sx={{ fontSize: 20, color: "warning.dark" }} />
                                    <ArrowDropDownIcon sx={{ fontSize: 20, color: "primary.main" }} />
                                </IconButton>
                                <Menu
                                    anchorEl={partnerAnchor}
                                    open={Boolean(partnerAnchor)}
                                    onClose={() => setPartnerAnchor(null)}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    {grants.map((g) => (
                                        <MenuItem
                                            key={g.partnerCode}
                                            selected={g.partnerCode === activePartnerCode}
                                            onClick={() => {
                                                setPartnerAnchor(null);
                                                if (g.partnerCode !== activePartnerCode) {
                                                    setActivePartnerCode(g.partnerCode);
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                                <Typography variant="body2" sx={{ flex: 1 }}>
                                                    {g.partnerName ?? g.partnerCode}
                                                </Typography>
                                                {g.partnerCode === activePartnerCode && (
                                                    <CheckIcon sx={{ fontSize: 18, color: "primary.main" }} />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </>
                        )}

                        <IconButton
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                            aria-label="user menu"
                            sx={{ p: 0.5 }}
                        >
                            <Avatar variant="circular">
                                <Typography sx={{ color: "primary.main" }}>{getInitials(user?.displayName, user?.email)}</Typography>
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={menuAnchor}
                            open={Boolean(menuAnchor)}
                            onClose={() => setMenuAnchor(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            <MenuItem onClick={() => { setMenuAnchor(null); router.push("/me"); }}>
                                Profile
                            </MenuItem>
                            <MenuItem onClick={async () => { setMenuAnchor(null); await logout(); }}>
                                Sign Out
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            <AnnouncementBanner />

            {/* Row below the AppBar: drawer + main content */}
            <Box className="print-layout-row" sx={{ display: "flex", flexGrow: 1, overflow: "hidden", background: "var(--background-default, #F5EDE5)" }}>

            {/* Permanent Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                    flexShrink: 0,
                    transition: TRANSITION,
                    "& .MuiDrawer-paper": {
                        position: "relative",
                        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
                        overflowX: "hidden",
                        transition: TRANSITION,
                        boxSizing: "border-box",
                        height: "100%",
                        border: "none",
                        boxShadow: "1px 0 3px rgba(0,0,0,0.08)",
                    },
                }}
            >

                {collapsed ? (
                    /* Collapsed: icon-only strip */
                    <List disablePadding sx={{ pt: 1 }}>
                        {flatNavItems.map((item) => {
                            const active = !item.disabled && isActive(pathname ?? "", item.href);
                            return (
                                <ListItem key={item.href} disablePadding sx={{ justifyContent: "center", py: 0.5 }}>
                                    <Tooltip title={item.label} placement="right">
                                        <span>
                                            <IconButton
                                                component={item.disabled ? "div" : Link}
                                                {...(!item.disabled && { href: item.href })}
                                                disabled={item.disabled}
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "100px",
                                                    color: "warning.dark",
                                                    ...(active && { bgcolor: "rgba(243,105,68,0.08)" }),
                                                }}
                                            >
                                                {item.icon}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : (
                    /* Expanded: grouped sections */
                    <List disablePadding sx={{ pt: 1 }}>
                        {navSections
                            .filter((section) => section.show)
                            .map((section) => {
                                const visibleItems = section.items.filter((item) => item.show);
                                if (visibleItems.length === 0) return null;
                                return (
                                    <Box
                                        key={section.heading}
                                        sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
                                    >
                                        <ListItem sx={{ px: 2, py: 0.5 }}>
                                            <Typography
                                                variant="overline"
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    letterSpacing: "1px",
                                                    lineHeight: 2.66,
                                                    color: "text.primary",
                                                }}
                                            >
                                                {section.heading}
                                            </Typography>
                                        </ListItem>
                                        {visibleItems.map((item) => {
                                            const active = !item.disabled && isActive(pathname ?? "", item.href);
                                            return (
                                                <ListItem key={item.href} disablePadding>
                                                    <ListItemButton
                                                        component={item.disabled ? "div" : Link}
                                                        {...(!item.disabled && { href: item.href })}
                                                        disabled={item.disabled}
                                                        sx={{
                                                            pl: 2,
                                                            py: 1,
                                                            ...(active && {
                                                                bgcolor: ACTIVE_BG,
                                                                borderRight: `3px solid ${ACTIVE_BORDER}`,
                                                            }),
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 48, color: "warning.dark" }}>
                                                            {item.icon}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={item.label}
                                                            slotProps={{
                                                                primary: {
                                                                    variant: "body2",
                                                                },
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                    </List>
                )}
            </Drawer>

            {/* Main content — key forces remount on partner switch so data re-fetches */}
            <Box key={activePartnerCode ?? ""} component="main" sx={{ flexGrow: 1, p: 3, overflow: "auto" }}>
                {!partnersLoading && children}
            </Box>

            </Box>
        </Box>
    );
}
